import React, { useState, useEffect, useCallback, useRef } from 'react';
import { sessionAPI, deviceAPI, planAPI, nasAPI, mikrotikAPI } from '../../services/api';
import { useBranchStore, useAuthStore } from '../../store';
import { toast } from 'react-toastify';
import AuthBadge, { authMeta } from '../../components/AuthMethodBadge/AuthMethodBadge';
import { guestIdentity, guestDetails } from '../../utils/guest';
import './DeviceAuth.css';

/* ── Helpers ───────────────────────────────────────────────────────────── */
function duration(start) {
    const ms = Date.now() - new Date(start).getTime();
    const s  = Math.floor(ms / 1000);
    const m  = Math.floor(s / 60);
    const h  = Math.floor(m / 60);
    if (h > 0) return `${h}h ${m % 60}m`;
    if (m > 0) return `${m}m ${s % 60}s`;
    return `${s}s`;
}
function fmtBytes(bytes) {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}
function fmtKbps(kbps) {
    if (!kbps) return '—';
    if (kbps >= 1024) return `${(kbps / 1024).toFixed(0)} Mbps`;
    return `${kbps} Kbps`;
}

/* ══════════════════════════════════════════════════════════════════════════ */
const DeviceAuth = () => {
    const { selectedBranch }   = useBranchStore();
    const { hasRole }          = useAuthStore();
    const canDisconnect        = hasRole('superadmin', 'tenant_admin', 'branch_admin', 'support');
    const canChangeplan        = hasRole('superadmin', 'tenant_admin', 'branch_admin');

    const [sessions,    setSessions]    = useState([]);
    const [devices,     setDevices]     = useState({});   // mac → Device record
    const [plans,       setPlans]       = useState([]);
    const [nasList,     setNasList]     = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [filterAuth,  setFilterAuth]  = useState('all');
    const [filterNas,   setFilterNas]   = useState('all');
    const [search,      setSearch]      = useState('');
    const [acting,      setActing]      = useState({});   // sessionId → bool
    const [tick,        setTick]        = useState(0);    // forces duration re-render every 10s

    /* Re-auth / plan-change modal */
    const [modal,       setModal]       = useState(null); // { session, mode: 'reauth'|'plan' }
    const [newPlanId,   setNewPlanId]   = useState('');
    const [applying,    setApplying]    = useState(false);

    /* ── Tab state ─────────────────────────────────────────────────────── */
    const [activeTab,   setActiveTab]   = useState('radius'); // 'radius' | 'live'

    /* ── Live-from-router state ─────────────────────────────────────────── */
    const [liveNasId,     setLiveNasId]     = useState('');
    const [liveData,      setLiveData]      = useState(null);  // { nasName, active:[], count }
    const [liveLoading,   setLiveLoading]   = useState(false);
    const [liveError,     setLiveError]     = useState('');
    const [kickingMac,    setKickingMac]    = useState('');
    const liveIntervalRef = useRef(null);

    const timerRef = useRef(null);

    /* ── Fetch ─────────────────────────────────────────────────────────── */
    const fetchAll = useCallback(async () => {
        try {
            const branchId = selectedBranch?._id;
            const [sessRes, devRes, planRes, nasRes] = await Promise.all([
                sessionAPI.getActive(branchId),
                deviceAPI.getAll({ sessionStatus: 'online', limit: 500, ...(branchId ? { branchId } : {}) }),
                planAPI.getAll(branchId),
                nasAPI.getAll(branchId ? { branchId } : {}),
            ]);

            setSessions(sessRes.data.data?.sessions || []);

            const devMap = {};
            (devRes.data?.data || []).forEach(d => { devMap[d.mac] = d; });
            setDevices(devMap);

            setPlans(planRes.data.data?.plans || []);
            setNasList(nasRes.data.data || []);
        } catch {
            /* silent — page stays stale until next auto-refresh */
        } finally {
            setLoading(false);
        }
    }, [selectedBranch]);

    useEffect(() => {
        setLoading(true);
        fetchAll();
        const interval = setInterval(fetchAll, 15000);
        return () => clearInterval(interval);
    }, [fetchAll]);

    /* tick every 10 s so duration column stays live */
    useEffect(() => {
        timerRef.current = setInterval(() => setTick(t => t + 1), 10000);
        return () => clearInterval(timerRef.current);
    }, []);

    /* ── Live router fetch ─────────────────────────────────────────────── */
    const fetchLive = useCallback(async (nasId) => {
        if (!nasId) return;
        setLiveLoading(true);
        setLiveError('');
        try {
            const res = await mikrotikAPI.getActiveUsers(nasId);
            setLiveData(res.data?.data || res.data || null);
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to fetch live sessions';
            setLiveError(msg);
            setLiveData(null);
        } finally {
            setLiveLoading(false);
        }
    }, []);

    useEffect(() => {
        if (liveIntervalRef.current) clearInterval(liveIntervalRef.current);
        if (activeTab === 'live' && liveNasId) {
            fetchLive(liveNasId);
            liveIntervalRef.current = setInterval(() => fetchLive(liveNasId), 10000);
        }
        return () => { if (liveIntervalRef.current) clearInterval(liveIntervalRef.current); };
    }, [activeTab, liveNasId, fetchLive]);

    const handleKick = async (mac) => {
        if (!liveNasId || !mac) return;
        if (!window.confirm(`Kick ${mac} from router?`)) return;
        setKickingMac(mac);
        try {
            await mikrotikAPI.kickUser(liveNasId, mac);
            toast.success(`${mac} kicked from router`);
            setTimeout(() => fetchLive(liveNasId), 1500);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Kick failed');
        } finally {
            setKickingMac('');
        }
    };

    /* ── Filter ────────────────────────────────────────────────────────── */
    const filtered = sessions.filter(s => {
        if (filterAuth !== 'all' && s.authMethod !== filterAuth) return false;
        if (filterNas  !== 'all' && s.nasIpAddress !== filterNas)  return false;
        if (search) {
            const q = search.toLowerCase();
            if (!( (s.deviceMac || '').toLowerCase().includes(q)
                || (s.userId?.name || '').toLowerCase().includes(q)
                || (guestIdentity(s.userId)?.label || '').toLowerCase().includes(q)
                || (s.userId?.mobile || '').toLowerCase().includes(q)
                || (s.framedIpAddress || '').includes(q)
            )) return false;
        }
        return true;
    });

    /* ── Stats ─────────────────────────────────────────────────────────── */
    const totalOnline  = sessions.length;
    const macCount     = sessions.filter(s => s.authMethod === 'mac').length;
    const portalCount  = sessions.filter(s => s.authMethod && s.authMethod !== 'mac').length;
    const bypassCount  = sessions.filter(s => {
        const dev = devices[(s.deviceMac || '').toUpperCase()];
        return dev?.bypassPortal;
    }).length;

    /* ── Actions ───────────────────────────────────────────────────────── */
    const handleDisconnect = async (session) => {
        if (!window.confirm(`Disconnect ${session.deviceMac}?`)) return;
        setActing(a => ({ ...a, [session._id]: 'disconnect' }));
        try {
            await sessionAPI.disconnect(session.sessionId || session._id);
            toast.success(`${session.deviceMac} disconnected`);
            fetchAll();
        } catch {
            toast.error('Disconnect failed');
        } finally {
            setActing(a => ({ ...a, [session._id]: null }));
        }
    };

    const handleReAuth = async (session) => {
        setActing(a => ({ ...a, [session._id]: 'reauth' }));
        try {
            await sessionAPI.disconnect(session.sessionId || session._id);
            toast.success(`Re-authorizing ${session.deviceMac} — device will reconnect with latest plan`);
            setTimeout(fetchAll, 3000);
        } catch {
            toast.error('Re-authorization failed');
        } finally {
            setActing(a => ({ ...a, [session._id]: null }));
        }
    };

    const openPlanModal = (session) => {
        const dev = devices[(session.deviceMac || '').toUpperCase()];
        setNewPlanId(dev?.planId?._id || session.planId?._id || '');
        setModal({ session, mode: 'plan' });
    };

    const handleApplyPlan = async () => {
        if (!modal) return;
        setApplying(true);
        try {
            const { session } = modal;
            const dev = devices[(session.deviceMac || '').toUpperCase()];
            const selectedPlan = plans.find(p => p._id === newPlanId);

            /* Update device plan in DB if device is registered */
            if (dev?._id) {
                await deviceAPI.update(dev._id, { planId: newPlanId || undefined });
            }
            /* CoA bandwidth update — applies immediately to live session */
            if (session.userId?._id && selectedPlan) {
                await sessionAPI.updateBandwidth(
                    session.userId._id,
                    selectedPlan.bandwidth?.uploadSpeed,
                    selectedPlan.bandwidth?.downloadSpeed
                );
            }
            toast.success('Plan updated and applied via CoA');
            setModal(null);
            fetchAll();
        } catch {
            toast.error('Failed to apply plan');
        } finally {
            setApplying(false);
        }
    };

    /* ── Unique NAS IPs for filter ─────────────────────────────────────── */
    const nasIps = [...new Set(sessions.map(s => s.nasIpAddress).filter(Boolean))];

    /* ── Auth method options for filter ───────────────────────────────── */
    const authMethods = [...new Set(sessions.map(s => s.authMethod).filter(Boolean))];

    /* ══════════════════════════════════════════════════════════════════════ */
    return (
        <div className="da-page">

            {/* ── Header ─────────────────────────────────────────────── */}
            <div className="da-header">
                <div>
                    <h1 className="da-title">
                        <i className="fas fa-shield-alt"></i> Device Authorization
                    </h1>
                    <p className="da-subtitle">Live view of every authenticated session — re-authorize, change plan, or disconnect</p>
                </div>
                <button
                    className="da-refresh-btn"
                    onClick={() => {
                        if (activeTab === 'live') fetchLive(liveNasId);
                        else { setLoading(true); fetchAll(); }
                    }}
                >
                    <i className="fas fa-sync-alt"></i> Refresh
                </button>
            </div>

            {/* ── Tabs ───────────────────────────────────────────────── */}
            <div className="da-tabs">
                <button
                    className={`da-tab ${activeTab === 'radius' ? 'da-tab-active' : ''}`}
                    onClick={() => setActiveTab('radius')}
                >
                    <i className="fas fa-database"></i> RADIUS Sessions
                </button>
                <button
                    className={`da-tab ${activeTab === 'live' ? 'da-tab-active' : ''}`}
                    onClick={() => setActiveTab('live')}
                >
                    <i className="fas fa-broadcast-tower"></i> Live from Router
                </button>
            </div>

            {activeTab === 'live' && (
                <div className="da-live-panel">
                    {/* NAS selector */}
                    <div className="da-live-toolbar">
                        <div className="da-live-toolbar-left">
                            <i className="fas fa-server" style={{ color: '#6366f1', marginRight: 8 }}></i>
                            <select
                                className="da-select"
                                value={liveNasId}
                                onChange={e => setLiveNasId(e.target.value)}
                            >
                                <option value="">— Select a Router —</option>
                                {nasList
                                    .filter(n => n.routerApi?.enabled)
                                    .map(n => (
                                        <option key={n._id} value={n._id}>
                                            {n.name} ({n.ipAddress})
                                        </option>
                                    ))
                                }
                            </select>
                            {liveData && (
                                <span className="da-live-badge">
                                    <i className="fas fa-circle" style={{ color: '#10b981', fontSize: 8 }}></i>
                                    {liveData.count ?? liveData.active?.length ?? 0} online — {liveData.nasName || ''}
                                </span>
                            )}
                        </div>
                        <span className="da-live-hint">Auto-refreshes every 10s</span>
                    </div>

                    {!liveNasId ? (
                        <div className="da-empty">
                            <i className="fas fa-server"></i>
                            <p>Select a router above to view live hotspot sessions</p>
                        </div>
                    ) : liveLoading && !liveData ? (
                        <div className="da-loading"><i className="fas fa-spinner fa-spin"></i> Connecting to router…</div>
                    ) : liveError ? (
                        <div className="da-live-error">
                            <i className="fas fa-exclamation-triangle"></i> {liveError}
                        </div>
                    ) : !liveData?.active?.length ? (
                        <div className="da-empty">
                            <i className="fas fa-wifi"></i>
                            <p>No active hotspot sessions on this router</p>
                        </div>
                    ) : (
                        <div className="da-table-wrap">
                            <table className="da-table">
                                <thead>
                                    <tr>
                                        <th>MAC Address</th>
                                        <th>User / Login</th>
                                        <th>IP Address</th>
                                        <th>Uptime</th>
                                        <th>Data Down</th>
                                        <th>Data Up</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {liveData.active.map((s, i) => (
                                        <tr key={s.macAddress || i} className="da-row">
                                            <td><span className="da-mono">{s.macAddress || '—'}</span></td>
                                            <td>
                                                <div className="da-user-name">{s.user || '—'}</div>
                                            </td>
                                            <td><span className="da-mono">{s.address || '—'}</span></td>
                                            <td>
                                                <span className="da-duration">{s.uptime || '—'}</span>
                                            </td>
                                            <td>
                                                <span className="da-data-cell">↓ {fmtBytes(s.bytesIn)}</span>
                                            </td>
                                            <td>
                                                <span className="da-data-cell">↑ {fmtBytes(s.bytesOut)}</span>
                                            </td>
                                            <td>
                                                {canDisconnect && (
                                                    <button
                                                        className="da-btn da-btn-disconnect"
                                                        onClick={() => handleKick(s.macAddress)}
                                                        disabled={kickingMac === s.macAddress}
                                                        title="Kick from router (force disconnect)"
                                                    >
                                                        {kickingMac === s.macAddress
                                                            ? <i className="fas fa-spinner fa-spin"></i>
                                                            : <i className="fas fa-ban"></i>}
                                                        {' '}Kick
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'radius' && <>

            {/* ── Stats row ──────────────────────────────────────────── */}
            <div className="da-stats">
                {[
                    { label: 'Total Online',    val: totalOnline,  icon: 'fa-wifi',           color: '#6366f1' },
                    { label: 'MAC Auth',        val: macCount,     icon: 'fa-network-wired',   color: '#f59e0b' },
                    { label: 'Portal Auth',     val: portalCount,  icon: 'fa-sign-in-alt',     color: '#10b981' },
                    { label: 'Bypass Devices',  val: bypassCount,  icon: 'fa-bolt',            color: '#8b5cf6' },
                ].map(s => (
                    <div className="da-stat-card" key={s.label}>
                        <div className="da-stat-icon" style={{ color: s.color, background: s.color + '18' }}>
                            <i className={`fas ${s.icon}`}></i>
                        </div>
                        <div>
                            <div className="da-stat-val">{s.val}</div>
                            <div className="da-stat-label">{s.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Filters ────────────────────────────────────────────── */}
            <div className="da-filters">
                <div className="da-search-wrap">
                    <i className="fas fa-search da-search-icon"></i>
                    <input
                        className="da-search"
                        placeholder="Search MAC, name, phone, IP…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <select className="da-select" value={filterAuth} onChange={e => setFilterAuth(e.target.value)}>
                    <option value="all">All Auth Methods</option>
                    {authMethods.map(m => (
                        <option key={m} value={m}>{authMeta(m).label}</option>
                    ))}
                </select>
                <select className="da-select" value={filterNas} onChange={e => setFilterNas(e.target.value)}>
                    <option value="all">All Routers</option>
                    {nasIps.map(ip => {
                        const n = nasList.find(n => n.ipAddress === ip || n.lastSeenIp === ip);
                        return <option key={ip} value={ip}>{n ? n.name : ip}</option>;
                    })}
                </select>
                <div className="da-count-tag">{filtered.length} session{filtered.length !== 1 ? 's' : ''}</div>
            </div>

            {/* ── Table ──────────────────────────────────────────────── */}
            {loading ? (
                <div className="da-loading">
                    <i className="fas fa-spinner fa-spin"></i> Loading sessions…
                </div>
            ) : filtered.length === 0 ? (
                <div className="da-empty">
                    <i className="fas fa-plug"></i>
                    <p>No active sessions{search || filterAuth !== 'all' ? ' matching filter' : ''}</p>
                </div>
            ) : (
                <div className="da-table-wrap">
                    <table className="da-table">
                        <thead>
                            <tr>
                                <th>Device</th>
                                <th>User / Guest</th>
                                <th>Auth Method</th>
                                <th>Plan &amp; Bandwidth</th>
                                <th>Data Used</th>
                                <th>Duration</th>
                                <th>IP Address</th>
                                <th>Router</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(session => {
                                const mac    = (session.deviceMac || '').toUpperCase();
                                const dev    = devices[mac];
                                const plan   = session.planId;
                                const dl     = plan?.bandwidth?.downloadSpeed;
                                const ul     = plan?.bandwidth?.uploadSpeed;
                                const nas    = nasList.find(n => n.ipAddress === session.nasIpAddress || n.lastSeenIp === session.nasIpAddress);
                                const isActing = acting[session._id];

                                return (
                                    <tr key={session._id} className="da-row">
                                        {/* Device */}
                                        <td>
                                            <div className="da-device-cell">
                                                <div className="da-device-icon">
                                                    <i className={`fas ${
                                                        dev?.type === 'tv'     ? 'fa-tv' :
                                                        dev?.type === 'camera' ? 'fa-camera' :
                                                        dev?.type === 'mobile' ? 'fa-mobile-alt' :
                                                        dev?.type === 'laptop' ? 'fa-laptop' :
                                                        'fa-microchip'
                                                    }`}></i>
                                                </div>
                                                <div>
                                                    <div className="da-device-name">
                                                        {dev?.name || <span className="da-muted">Unknown</span>}
                                                        {dev?.bypassPortal && (
                                                            <span className="da-bypass-chip">
                                                                <i className="fas fa-bolt"></i> Bypass
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="da-device-mac">{session.deviceMac || '—'}</div>
                                                    {dev?.type && <div className="da-device-type">{dev.type}</div>}
                                                </div>
                                            </div>
                                        </td>

                                        {/* User */}
                                        <td>
                                            {session.userId ? (
                                                // The guest — name, else room, else mobile (utils/guest): never
                                                // "Guest Room 302" or its stand-in "guest-…" mobile.
                                                <div>
                                                    <div className="da-user-name">{guestIdentity(session.userId)?.label || 'Guest'}</div>
                                                    <div className="da-user-phone">{guestDetails(session.userId).join(' · ')}</div>
                                                </div>
                                            ) : session.authMethod === 'mac' ? (
                                                <span className="da-mac-tag"><i className="fas fa-network-wired"></i> MAC Auth</span>
                                            ) : (
                                                <span className="da-muted">—</span>
                                            )}
                                        </td>

                                        {/* Auth method */}
                                        <td>
                                            <AuthBadge method={session.authMethod} />
                                        </td>

                                        {/* Plan + bandwidth */}
                                        <td>
                                            {plan ? (
                                                <div>
                                                    <div className="da-plan-name">{plan.name}</div>
                                                    {(dl || ul) && (
                                                        <div className="da-speed">
                                                            <span>↓ {fmtKbps(dl)}</span>
                                                            <span>↑ {fmtKbps(ul)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : dev?.bypassPortal ? (
                                                <span className="da-unres-tag"><i className="fas fa-infinity"></i> Unrestricted</span>
                                            ) : (
                                                <span className="da-muted">—</span>
                                            )}
                                        </td>

                                        {/* Data used */}
                                        <td>
                                            <div className="da-data">
                                                <span>↓ {fmtBytes(session.dataReceived)}</span>
                                                <span>↑ {fmtBytes(session.dataTransmitted)}</span>
                                            </div>
                                        </td>

                                        {/* Duration (live) */}
                                        <td>
                                            <span className="da-duration" data-tick={tick}>
                                                {session.startTime ? duration(session.startTime) : '—'}
                                            </span>
                                        </td>

                                        {/* IP */}
                                        <td>
                                            <span className="da-mono">{session.framedIpAddress || '—'}</span>
                                        </td>

                                        {/* Router */}
                                        <td>
                                            <div className="da-nas">
                                                <span className="da-nas-name">{nas?.name || session.nasIpAddress || '—'}</span>
                                                {session.nasIpAddress && <span className="da-nas-ip">{session.nasIpAddress}</span>}
                                            </div>
                                        </td>

                                        {/* Actions */}
                                        <td>
                                            <div className="da-actions">
                                                {canChangeplan && (
                                                    <button
                                                        className="da-btn da-btn-plan"
                                                        onClick={() => openPlanModal(session)}
                                                        disabled={!!isActing}
                                                        title="Change / Apply Plan"
                                                    >
                                                        <i className="fas fa-exchange-alt"></i> Plan
                                                    </button>
                                                )}
                                                {canDisconnect && (
                                                    <>
                                                        <button
                                                            className="da-btn da-btn-reauth"
                                                            onClick={() => handleReAuth(session)}
                                                            disabled={!!isActing}
                                                            title="Re-authorize (CoA disconnect → reconnect with latest plan)"
                                                        >
                                                            {isActing === 'reauth'
                                                                ? <i className="fas fa-spinner fa-spin"></i>
                                                                : <i className="fas fa-redo"></i>}
                                                            {isActing === 'reauth' ? '…' : 'Re-auth'}
                                                        </button>
                                                        <button
                                                            className="da-btn da-btn-disconnect"
                                                            onClick={() => handleDisconnect(session)}
                                                            disabled={!!isActing}
                                                            title="Disconnect session"
                                                        >
                                                            {isActing === 'disconnect'
                                                                ? <i className="fas fa-spinner fa-spin"></i>
                                                                : <i className="fas fa-power-off"></i>}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Plan Change Modal ───────────────────────────────────── */}
            {modal && (
                <div className="da-modal-overlay" onClick={() => setModal(null)}>
                    <div className="da-modal" onClick={e => e.stopPropagation()}>
                        <div className="da-modal-header">
                            <h3><i className="fas fa-exchange-alt"></i> Change Plan</h3>
                            <button className="da-modal-close" onClick={() => setModal(null)}>×</button>
                        </div>
                        <div className="da-modal-body">
                            <div className="da-modal-device">
                                <div className="da-modal-mac">{modal.session.deviceMac}</div>
                                {guestIdentity(modal.session.userId)?.label && (
                                    <div className="da-modal-user">{guestIdentity(modal.session.userId).label}</div>
                                )}
                                <AuthBadge method={modal.session.authMethod} />
                            </div>
                            <label className="da-modal-label">Select New Plan</label>
                            <select
                                className="da-modal-select"
                                value={newPlanId}
                                onChange={e => setNewPlanId(e.target.value)}
                            >
                                <option value="">— No plan (Unrestricted) —</option>
                                {plans.map(p => (
                                    <option key={p._id} value={p._id}>
                                        {p.name}
                                        {p.bandwidth?.downloadSpeed && ` — ↓${fmtKbps(p.bandwidth.downloadSpeed)} / ↑${fmtKbps(p.bandwidth.uploadSpeed)}`}
                                    </option>
                                ))}
                            </select>
                            <p className="da-modal-hint">
                                <i className="fas fa-info-circle"></i> The plan is applied immediately via CoA — no reconnect needed for portal-auth users. MAC auth devices re-authenticate on next connect.
                            </p>
                        </div>
                        <div className="da-modal-footer">
                            <button className="da-btn-cancel" onClick={() => setModal(null)}>Cancel</button>
                            <button
                                className="da-btn-apply"
                                onClick={handleApplyPlan}
                                disabled={applying}
                            >
                                {applying
                                    ? <><i className="fas fa-spinner fa-spin"></i> Applying…</>
                                    : <><i className="fas fa-check"></i> Apply Plan</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            </>}
        </div>
    );
};

export default DeviceAuth;
